import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Zap, Pencil, Save, X, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const response = await fetch(`${API}/admin/payouts`, {
        headers: getAuthHeaders(),
        
      });
      
      if (!response.ok) throw new Error('Failed to fetch payouts');
      
      const data = await response.json();
      setPayouts(data);
    } catch (error) {
      toast.error('Failed to load payouts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePayout = async (payoutId, updates) => {
    try {
      const response = await fetch(`${API}/admin/payouts/${payoutId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) throw new Error('Failed to update payout');
      
      if (updates.status) {
        toast.success(`Payout ${updates.status}`);
      } else if (updates.amount !== undefined) {
        toast.success('Amount updated');
      }
      fetchPayouts();
      setEditingId(null);
    } catch (error) {
      toast.error('Failed to update payout');
    }
  };

  const startEditing = (payout) => {
    setEditingId(payout.payout_id);
    setEditAmount(payout.amount.toString());
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditAmount('');
  };

  const saveAmount = (payoutId) => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    updatePayout(payoutId, { amount });
  };

  const sendPayoutReport = async () => {
    setSendingReport(true);
    try {
      const response = await fetch(`${API}/admin/payouts/send-report`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send report');
      }
      
      const data = await response.json();
      toast.success(data.message || 'Payout report sent successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to send payout report');
    } finally {
      setSendingReport(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-orange-500" />;
    }
  };

  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const processedPayouts = payouts.filter(p => p.status !== 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Payout Requests
          </h1>
          <p className="text-muted-foreground">Approve or reject partner payout requests</p>
        </div>
        <Button 
          onClick={sendPayoutReport}
          disabled={sendingReport}
          variant="outline"
          data-testid="send-payout-report-button"
        >
          <Mail className="h-4 w-4 mr-2" />
          {sendingReport ? 'Sending...' : 'Send Payout Report'}
        </Button>
      </div>

      {/* Pending Payouts - Approval Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            Pending Approval ({pendingPayouts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingPayouts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No pending payouts</p>
          ) : (
            <div className="space-y-4">
              {pendingPayouts.map((payout) => (
                <div
                  key={payout.payout_id}
                  data-testid={`payout-${payout.payout_id}`}
                  className="flex items-center justify-between p-4 border border-orange-200 bg-orange-50/50 rounded-lg hover:bg-orange-50 transition-colors"
                >
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(payout.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{payout.partner_name}</p>
                          {payout.auto_generated && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              <Zap className="h-3 w-3 mr-1" />
                              Auto
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{payout.partner_email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Amount:</span>
                        {editingId === payout.payout_id ? (
                          <div className="flex items-center gap-2">
                            <span>₹</span>
                            <Input
                              type="number"
                              value={editAmount}
                              onChange={(e) => setEditAmount(e.target.value)}
                              className="w-24 h-7 text-sm"
                              min="0"
                              step="0.01"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => saveAmount(payout.payout_id)}
                            >
                              <Save className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={cancelEditing}
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${payout.amount > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                              ₹{payout.amount.toFixed(2)}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => startEditing(payout)}
                            >
                              <Pencil className="h-3 w-3 text-gray-500" />
                            </Button>
                            {payout.amount === 0 && (
                              <span className="text-xs text-orange-600">(Set amount)</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Bank:</span>
                        <span className="ml-2">{payout.bank_name || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Account:</span>
                        <span className="ml-2">
                          {payout.account_number && payout.account_number !== 'Not provided' 
                            ? `****${payout.account_number.slice(-4)}` 
                            : 'Not provided'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Requested:</span>
                        <span className="ml-2">{new Date(payout.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <div className="flex gap-2">
                      <Button
                        data-testid={`approve-${payout.payout_id}`}
                        size="sm"
                        onClick={() => updatePayout(payout.payout_id, { status: 'completed' })}
                        className="bg-green-600 hover:bg-green-700"
                        disabled={payout.amount === 0}
                      >
                        Approve
                      </Button>
                      <Button
                        data-testid={`reject-${payout.payout_id}`}
                        size="sm"
                        variant="destructive"
                        onClick={() => updatePayout(payout.payout_id, { status: 'rejected' })}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processed Payouts */}
      <Card>
        <CardHeader>
          <CardTitle>Processed Payouts ({processedPayouts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {processedPayouts.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No processed payouts yet</p>
          ) : (
            <div className="space-y-4">
              {processedPayouts.map((payout) => (
                <div
                  key={payout.payout_id}
                  data-testid={`payout-${payout.payout_id}`}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(payout.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{payout.partner_name}</p>
                          {payout.auto_generated && (
                            <Badge variant="outline" className="text-xs">
                              <Zap className="h-3 w-3 mr-1" />
                              Auto
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{payout.partner_email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm mt-2">
                      <div>
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="font-semibold ml-2">₹{payout.amount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Bank:</span>
                        <span className="ml-2">{payout.bank_name || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Account:</span>
                        <span className="ml-2">
                          {payout.account_number && payout.account_number !== 'Not provided' 
                            ? `****${payout.account_number.slice(-4)}` 
                            : 'Not provided'}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Processed:</span>
                        <span className="ml-2">{new Date(payout.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Badge variant={payout.status === 'completed' ? 'default' : 'destructive'}>
                      {payout.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
